import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, InputNumber, Radio, Typography } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import {
  applyRegistryToGroupedNodeFields,
  buildRegistryPayload,
  probeImageAndValidateArchitecture,
  readFormImageSourceUrl,
  readRegistryAuthFromValues,
  resolveRegistryHost,
  setFormFieldError,
} from '@/delivery-type-plugin/capabilities/image';
import { maasInstanceReplicasRules } from '@/delivery-type-plugin/capabilities/maas-init/form-messages';
import type { MaasOptions, MaasSharedFormValues } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { DEFAULT_MAAS_OPTIONS, MaasRunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import {
  formJsonFieldFormatter,
  formJsonFieldNormalize,
  parseFormJsonField,
} from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import {
  clearContainerGpuResourceConfigurationsCache,
  collectBaselineNodesFromInitValues,
  getCachedContainerGpuResourceConfigurations,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/gpuResourceValidation';
import { buildRunModeOptions } from '@/delivery-type-plugin/capabilities/maas-node-groups/labels';
import { INFERENCE_SYSTEM_FIELD } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import type { EmbeddedNodeSpec } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import { parseNodesField } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import {
  DEFAULT_MAAS_NODE_GROUP_FIELD_PATHS,
  type MaasNodeGroupFieldPaths,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeGroupFieldPaths';
import type { ContainerDeployInstanceScheduling } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigCore';
import {
  IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD,
  IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD,
} from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigFields';
import type { ContainerDeployStartupCommands } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigForm';
import { useResourceSectionReadOnly, useSceneLayoutContext } from '@/delivery-type-plugin/components';
import { isOrderPresetFieldLocked } from '@/delivery-type-plugin/platform/access-policy';
import stepStyles from '@/delivery-type-plugin/styles/maas-model-step.module.less';

import { GroupAddCard, GroupSpecCard, GroupSpecSection } from '../group-spec-card';
import styles from '../group-spec-card/group-spec-card.module.less';
import { GroupSpecDrawer } from '../group-spec-modal';
import type { MaasNodeGroupsBlockOptions } from '../types';
import { useGroupsConfigState } from './useGroupsConfigState';
import { validateMasterNodesFieldWithGpuLatest, validateWorkerNodesFieldLatest } from './validators';

const FormItem = Form.Item;

/** 提交/切步时再校验 */
const VALIDATE_ON_SUBMIT: string[] = [];

function parseSystemDiskGi(raw: unknown): number | undefined {
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

function resolveNodeSystemDiskGi(node: EmbeddedNodeSpec | undefined, sharedGi?: number): number | undefined {
  return parseSystemDiskGi(node?._systemDiskSize) ?? sharedGi;
}

export interface MaasGroupsConfigSectionProps extends MaasNodeGroupsBlockOptions {
  form: FormInstance;
}

/**
 * 节点配置：UI 仅维护 masterNodes / workerNodes JSON 与系统盘字段，提交时再组装 API 结构
 */
export function MaasGroupsConfigSection({
  form,
  namespace,
  isEdit,
  initValues,
  typeOptions = DEFAULT_MAAS_OPTIONS,
  billModelId,
  deploymentTypeId,
  paymentTiming,
  skuType,
  resourceEditMode,
  lockActionsWhenFilled,
  initCommandScriptHint,
  initCommandSectionDesc,
  startupCommandSectionDesc,
  initCommandEditor = 'script',
  showNodeStartupCommand = false,
  showInstanceReplicas = false,
  getDefaultGroupName,
  masterGroupTitle,
  masterGroupRoleLabel,
  onRunModeChange,
  embeddedNodeConfig,
  showInferenceFramework,
  showTrainingImage,
  showService,
  showHealthCheck,
  showAdvancedConfig,
  showNic,
  customImageOnly,
  presetArchitecture,
  presetImageLocked,
  nicPricingOptions,
  requireGpuSoftware,
  fieldPaths: fieldPathsProp,
  sectionTitle,
}: MaasGroupsConfigSectionProps) {
  const { t } = useTranslation();
  const fieldPaths: MaasNodeGroupFieldPaths = fieldPathsProp ?? DEFAULT_MAAS_NODE_GROUP_FIELD_PATHS;
  const sectionReadOnly = useResourceSectionReadOnly();
  const { presetConfigSnapshot } = useSceneLayoutContext();
  /** 固定规格下单：整块只读（含空态添加入口、实例数、部署模式） */
  const isOrderFixed = resourceEditMode === 'order-fixed';
  const fullyReadOnly = sectionReadOnly || isOrderFixed;
  const replicasField = fieldPaths.replicas ?? 'replicas';
  /** 优先已裁剪的 presetConfigSnapshot，避免未勾选模块残留 initValues 误锁副本 */
  const presetForReplicasLock = presetConfigSnapshot ?? (initValues as Record<string, unknown> | undefined);
  const instanceReplicasLocked = fullyReadOnly || isOrderPresetFieldLocked(resourceEditMode, presetForReplicasLock, replicasField);

  useEffect(() => {
    void getCachedContainerGpuResourceConfigurations();
    return () => clearContainerGpuResourceConfigurationsCache();
  }, []);

  const runMode = Form.useWatch(fieldPaths.runMode, form) as MaasRunMode | undefined;
  const masterSystemDiskGi = parseSystemDiskGi(Form.useWatch(fieldPaths.masterSystemDisk, form));
  const workerSystemDiskGi = parseSystemDiskGi(Form.useWatch(fieldPaths.workerSystemDisk, form));
  const inferenceSystemRaw = Form.useWatch(INFERENCE_SYSTEM_FIELD, form);
  const inferenceSystem = useMemo(() => parseFormJsonField<Record<string, unknown>>(inferenceSystemRaw), [inferenceSystemRaw]);
  const imageContainerDeployInstanceSchedulingRaw = Form.useWatch(IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD, form);
  const imageContainerDeployInstanceScheduling = useMemo(
    () => parseFormJsonField<ContainerDeployInstanceScheduling>(imageContainerDeployInstanceSchedulingRaw),
    [imageContainerDeployInstanceSchedulingRaw],
  );
  const imageContainerDeployStartupRaw = Form.useWatch(IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD, form);
  const imageContainerDeployStartup = useMemo(
    () => parseFormJsonField<ContainerDeployStartupCommands>(imageContainerDeployStartupRaw),
    [imageContainerDeployStartupRaw],
  );
  const {
    masterNodes,
    workerNodes,
    nodeModalVisible,
    modalRole,
    editingIndex,
    isPrimarySecondary,
    modalInitial,
    masterSpecForCard,
    workerModalReplicasMin,
    handleRunModeChange,
    openMasterModal,
    openWorkerModal,
    handleNodeModalOk,
    handleDeleteWorker,
    closeNodeModal,
  } = useGroupsConfigState({
    form,
    runMode,
    isEdit,
    initValues,
    onRunModeChange,
    fieldPaths,
  });
  const [openingNodeModal, setOpeningNodeModal] = useState(false);
  const shouldProbeBeforeOpen = !showInferenceFramework && !showTrainingImage && !customImageOnly;

  const probeThenOpenNodeModal = useCallback(
    async (open: () => void) => {
      if (openingNodeModal) return;
      if (!shouldProbeBeforeOpen) {
        open();
        return;
      }
      setOpeningNodeModal(true);
      try {
        const values = form.getFieldsValue() as Record<string, unknown>;
        const sourceUrl = readFormImageSourceUrl(
          form,
          [`${INFERENCE_SYSTEM_FIELD}.image`, `${INFERENCE_SYSTEM_FIELD}.url`],
          INFERENCE_SYSTEM_FIELD,
        );
        const registryAuth = readRegistryAuthFromValues(values);
        const probed = await probeImageAndValidateArchitecture({
          sourceUrl,
          registryAuth,
          requireArchitecture: false,
          sourceUrlField: `${INFERENCE_SYSTEM_FIELD}.image`,
          emptySourceMessage: t('data-center:please.enter.image'),
          t,
        });
        if (probed.ok === false) {
          setFormFieldError(form, probed.field || `${INFERENCE_SYSTEM_FIELD}.image`, probed.message);
          return;
        }
        const registry = buildRegistryPayload(resolveRegistryHost(probed.registryHost, sourceUrl), registryAuth);
        applyRegistryToGroupedNodeFields(form, registry);
        open();
      } finally {
        setOpeningNodeModal(false);
      }
    },
    [form, openingNodeModal, shouldProbeBeforeOpen, t],
  );

  const handleOpenMasterModal = useCallback(() => {
    void probeThenOpenNodeModal(openMasterModal);
  }, [openMasterModal, probeThenOpenNodeModal]);

  const handleOpenWorkerModal = useCallback(
    (index = -1) => {
      void probeThenOpenNodeModal(() => openWorkerModal(index));
    },
    [openWorkerModal, probeThenOpenNodeModal],
  );
  /** 自定义/后付费下单：SKU 预置已有节点组时禁止改删，仍可添加；实例数不受此锁限制 */
  const presetHasNodeGroups = useMemo(() => {
    if (!initValues) return false;
    if (
      parseNodesField(initValues.masterNodes).length > 0 ||
      parseNodesField(initValues.workerNodes).length > 0 ||
      parseNodesField(initValues.prefillMasterNodes).length > 0 ||
      parseNodesField(initValues.prefillWorkerNodes).length > 0 ||
      parseNodesField(initValues.decodeMasterNodes).length > 0 ||
      parseNodesField(initValues.decodeWorkerNodes).length > 0
    ) {
      return true;
    }
    // 兼容预置仅含 API 组结构（无 masterNodes/workerNodes JSON）
    if (initValues.headGroupSpec && typeof initValues.headGroupSpec === 'object') return true;
    const workers = initValues.workerGroupSpecs;
    return Array.isArray(workers) && workers.length > 0;
  }, [initValues]);
  const nodeActionsLocked = Boolean(lockActionsWhenFilled) && presetHasNodeGroups && !fullyReadOnly;
  const nodeCardReadonly = fullyReadOnly || nodeActionsLocked;
  const runModeLocked = fullyReadOnly || nodeActionsLocked;
  const runModeOptions = useMemo(
    () => buildRunModeOptions(typeOptions.runMode ?? DEFAULT_MAAS_OPTIONS.runMode, t),
    [typeOptions.runMode, t],
  );
  const gpuValidationOptions = useMemo(
    () => (isEdit ? { baselineNodes: collectBaselineNodesFromInitValues(initValues) } : undefined),
    [initValues, isEdit],
  );

  const isNormalSingleTopology = workerNodes.length <= 1 && (workerNodes[0]?.replicas ?? 1) <= 1;
  const workerGroupSectionHint = isNormalSingleTopology
    ? t('data-center:maas.worker.group.section.hint.single')
    : t('data-center:maas.worker.group.section.hint.multiple');

  const modalPeerNodes = useMemo(() => {
    if (modalRole === 'master') {
      return workerNodes;
    }
    const masters = isPrimarySecondary ? masterNodes : [];
    if (editingIndex >= 0) {
      return [...masters, ...workerNodes.filter((_, index) => index !== editingIndex)];
    }
    return [...masters, ...workerNodes];
  }, [editingIndex, isPrimarySecondary, masterNodes, modalRole, workerNodes]);

  const renderWorkerGroup = () => (
    <>
      <GroupSpecSection title={t('data-center:maas.worker.group')} hint={workerGroupSectionHint}>
        <div className={styles.cardList}>
          {workerNodes.length === 0 ? (
            <GroupSpecCard
              role="worker"
              spec={undefined}
              onEdit={() => handleOpenWorkerModal(-1)}
              readonly={fullyReadOnly || openingNodeModal}
            />
          ) : (
            workerNodes.map((node, index) => (
              <GroupSpecCard
                key={`worker-${node.name}-${index}`}
                role="worker"
                spec={node}
                systemDiskGi={embeddedNodeConfig ? resolveNodeSystemDiskGi(node) : workerSystemDiskGi}
                onEdit={() => handleOpenWorkerModal(index)}
                onDelete={!nodeCardReadonly && workerNodes.length > 1 ? () => handleDeleteWorker(index) : undefined}
                readonly={nodeCardReadonly}
                showNodeStartupCommand={showNodeStartupCommand}
              />
            ))
          )}
          {/* 固定规格全锁不展示添加；自定义/后付费有数据时仍可添加 */}
          {workerNodes.length > 0 && !fullyReadOnly ? <GroupAddCard onClick={() => handleOpenWorkerModal(-1)} /> : null}
        </div>
        <FormItem shouldUpdate noStyle>
          {(_, formInstance) => {
            const error = formInstance.getFieldError(fieldPaths.workerNodes);
            if (!error?.message) return null;
            return (
              <Typography.Text type="error" className={styles.fieldError}>
                {error.message}
              </Typography.Text>
            );
          }}
        </FormItem>
      </GroupSpecSection>
    </>
  );

  return (
    <>
      <div className={styles.groupsPanel}>
        {showInstanceReplicas ? (
          <FormItem
            field={replicasField}
            label={t('data-center:maas.instance.count')}
            initialValue={1}
            rules={maasInstanceReplicasRules(t('data-center:maas.instance.count'), 0)}
            disabled={instanceReplicasLocked}
          >
            <InputNumber min={0} precision={0} mode="button" disabled={instanceReplicasLocked} />
          </FormItem>
        ) : null}
        {sectionTitle ? (
          <Typography.Title heading={6} style={{ marginTop: 0, marginBottom: 0 }}>
            {sectionTitle}
          </Typography.Title>
        ) : null}
        <FormItem field={fieldPaths.runMode} initialValue={MaasRunMode.Normal} hidden>
          <span />
        </FormItem>
        <FormItem
          label={t('data-center:maas.deploy.mode')}
          layout="horizontal"
          labelAlign="left"
          labelCol={{ flex: 'none' }}
          wrapperCol={{ flex: 'none' }}
          className={stepStyles.deployModeRow}
        >
          <Radio.Group
            type="button"
            size="small"
            value={runMode}
            onChange={handleRunModeChange}
            options={runModeOptions}
            disabled={runModeLocked}
          />
        </FormItem>

        <FormItem
          field={fieldPaths.masterNodes}
          hidden
          validateTrigger={VALIDATE_ON_SUBMIT}
          rules={[
            {
              async validator(value, callback) {
                try {
                  const mode = form.getFieldValue(fieldPaths.runMode) as MaasRunMode | undefined;
                  const workerValue = form.getFieldValue(fieldPaths.workerNodes);
                  const error = await validateMasterNodesFieldWithGpuLatest(value, workerValue, mode, t, gpuValidationOptions);
                  callback(error);
                } catch (error) {
                  console.log(error);
                  callback();
                }
              },
            },
          ]}
          style={{ display: 'none', height: 0, margin: 0 }}
        >
          <span />
        </FormItem>
        <FormItem
          field={fieldPaths.workerNodes}
          hidden
          validateTrigger={VALIDATE_ON_SUBMIT}
          rules={[
            {
              async validator(value, callback) {
                try {
                  const mode = form.getFieldValue(fieldPaths.runMode) as MaasRunMode | undefined;
                  const masterValue = form.getFieldValue(fieldPaths.masterNodes);
                  const error = await validateWorkerNodesFieldLatest(value, mode, t, { masterValue, gpuOptions: gpuValidationOptions });
                  callback(error);
                } catch (error) {
                  console.log(error);
                  callback();
                }
              },
            },
          ]}
          style={{ display: 'none', height: 0, margin: 0 }}
        >
          <span />
        </FormItem>

        {isPrimarySecondary ? (
          <>
            <GroupSpecSection
              title={masterGroupTitle ?? t('data-center:maas.master.group')}
              hint={t('data-center:maas.master.group.section.hint')}
            >
              <GroupSpecCard
                role="master"
                masterRoleLabel={masterGroupRoleLabel}
                spec={masterSpecForCard}
                systemDiskGi={embeddedNodeConfig ? resolveNodeSystemDiskGi(masterSpecForCard) : masterSystemDiskGi}
                onEdit={handleOpenMasterModal}
                readonly={masterSpecForCard ? nodeCardReadonly : fullyReadOnly}
                showNodeStartupCommand={showNodeStartupCommand}
              />
              <FormItem shouldUpdate noStyle>
                {(_, formInstance) => {
                  const error = formInstance.getFieldError(fieldPaths.masterNodes);
                  if (!error?.message) return null;
                  return (
                    <Typography.Text type="error" className={styles.fieldError}>
                      {error.message}
                    </Typography.Text>
                  );
                }}
              </FormItem>
            </GroupSpecSection>
          </>
        ) : null}

        {renderWorkerGroup()}
      </div>

      <FormItem
        field={IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD}
        hidden
        style={{ display: 'none', margin: 0 }}
        triggerPropName="value"
        formatter={formJsonFieldFormatter}
        normalize={formJsonFieldNormalize}
      >
        <Input />
      </FormItem>
      <FormItem
        field={IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD}
        hidden
        style={{ display: 'none', margin: 0 }}
        triggerPropName="value"
        formatter={formJsonFieldFormatter}
        normalize={formJsonFieldNormalize}
      >
        <Input />
      </FormItem>

      <GroupSpecDrawer
        visible={nodeModalVisible}
        role={modalRole}
        initialValues={modalInitial}
        hideReplicas={false}
        replicasMin={workerModalReplicasMin}
        siblingWorkers={modalRole === 'worker' ? workerNodes : undefined}
        editingWorkerIndex={modalRole === 'worker' ? editingIndex : -1}
        forbidReservedWorkerNames={isPrimarySecondary && modalRole === 'worker'}
        namespace={namespace}
        isEdit={isEdit}
        baselineNodes={gpuValidationOptions?.baselineNodes}
        onOk={handleNodeModalOk}
        onCancel={closeNodeModal}
        parentForm={form}
        systemDiskSizeField={modalRole === 'master' ? fieldPaths.masterSystemDisk : fieldPaths.workerSystemDisk}
        systemDiskPricingField={modalRole === 'master' ? fieldPaths.masterSystemDiskPricing : fieldPaths.workerSystemDiskPricing}
        systemDiskRoleLabel={modalRole === 'master' ? (masterGroupRoleLabel ?? 'Master') : 'Worker'}
        presetConfigSnapshot={presetConfigSnapshot}
        inferenceSystem={inferenceSystem}
        instanceScheduling={imageContainerDeployInstanceScheduling}
        peerNodes={modalPeerNodes}
        billModelId={billModelId}
        deploymentTypeId={deploymentTypeId}
        paymentTiming={paymentTiming}
        skuType={skuType}
        resourceEditMode={resourceEditMode}
        lockActionsWhenFilled={lockActionsWhenFilled}
        initCommandScriptHint={initCommandScriptHint}
        initCommandSectionDesc={initCommandSectionDesc}
        startupCommandSectionDesc={startupCommandSectionDesc}
        initCommandEditor={initCommandEditor}
        showNodeStartupCommand={showNodeStartupCommand}
        imageContainerDeployStartup={imageContainerDeployStartup}
        defaultName={getDefaultGroupName?.(modalRole, modalRole === 'master' ? 0 : editingIndex >= 0 ? editingIndex : workerNodes.length)}
        masterGroupTitle={masterGroupTitle}
        embeddedNodeConfig={embeddedNodeConfig}
        showInferenceFramework={showInferenceFramework}
        showTrainingImage={showTrainingImage}
        showService={showService}
        showHealthCheck={showHealthCheck}
        showAdvancedConfig={showAdvancedConfig ?? showHealthCheck}
        showNic={showNic}
        customImageOnly={customImageOnly}
        presetArchitecture={presetArchitecture}
        presetImageLocked={presetImageLocked}
        nicPricingOptions={nicPricingOptions}
        requireGpuSoftware={requireGpuSoftware}
      />
    </>
  );
}

export default MaasGroupsConfigSection;
